import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { mapDetails } from './referenceFormHelpers';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { citiesApi, type City, type CityInput } from '../../lib/api/reference';
import styles from './ReferenceFormDialog.module.css';

/**
 * The create/edit form for a city. See `DispensaryFormDialog.tsx` for the
 * shared reasoning (one component for both modes, no diffing, every field
 * resent on save).
 *
 * NO DISPENSARY PICKER. Cities are global — one row per real place — since
 * the API's `make_cities_global` migration; a city used to be entered once
 * per dispensary, which is how 98 real cities came to fill 288 rows.
 * Which dispensaries serve a city follows from where their centres are, so
 * that choice lives on `CentreFormDialog`, not here. A duplicate name or
 * code answers a `422` naming the field (`CityRequestBase`), rendered under
 * that field like any other server-side validation error.
 */

export interface CityFormDialogProps {
  open: boolean;
  node?: City;
  onClose: () => void;
  onSaved: (node: City) => void;
}

interface FormState {
  externalCode: string;
  nameEn: string;
  nameAr: string;
  isActive: boolean;
}

function stateFor(node: City | undefined): FormState {
  if (node === undefined) {
    return { externalCode: '', nameEn: '', nameAr: '', isActive: true };
  }
  return {
    externalCode: node.external_code,
    nameEn: node.name_en,
    nameAr: node.name_ar,
    isActive: node.is_active,
  };
}

type MissingField = 'external_code' | 'name_en' | 'name_ar';

const FORBIDDEN_CODE = 'forbidden';

export function CityFormDialog({ open, node, onClose, onSaved }: CityFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = node !== undefined;

  const [state, setState] = useState<FormState>(() => stateFor(node));
  const [missing, setMissing] = useState<readonly MissingField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(stateFor(node));
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, node]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('reference.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('reference.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('reference.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const empty: MissingField[] = [];
    if (state.externalCode.trim() === '') empty.push('external_code');
    if (state.nameEn.trim() === '') empty.push('name_en');
    if (state.nameAr.trim() === '') empty.push('name_ar');

    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const input: CityInput = {
        external_code: state.externalCode.trim(),
        name_en: state.nameEn.trim(),
        name_ar: state.nameAr.trim(),
        is_active: state.isActive,
      };
      const saved =
        isEdit && node !== undefined
          ? await citiesApi.update(node.uuid, input)
          : await citiesApi.create(input);
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const dialogTitle =
    isEdit && node !== undefined
      ? t('reference.editCityTitle', { name: node.name_en })
      : t('reference.createCityTitle');

  return (
    <Dialog open={open} size="lg" onClose={onClose} initialFocusRef={codeRef} title={dialogTitle}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div
          className={styles['failure']}
          role="alert"
          aria-atomic="true"
          data-testid="cityFormFailure"
        >
          {generalFailure}
        </div>

        <Input
          ref={codeRef}
          label={t('reference.fieldCode')}
          width="code"
          dir="ltr"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={state.externalCode}
          error={
            missing.includes('external_code')
              ? t('reference.required')
              : fieldErrors['external_code']
          }
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, externalCode: value }));
          }}
        />

        <Input
          label={t('reference.fieldNameEn')}
          width="fill"
          dir="ltr"
          required
          value={state.nameEn}
          error={missing.includes('name_en') ? t('reference.required') : fieldErrors['name_en']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, nameEn: value }));
          }}
        />

        <Input
          label={t('reference.fieldNameAr')}
          width="fill"
          dir="rtl"
          required
          value={state.nameAr}
          error={missing.includes('name_ar') ? t('reference.required') : fieldErrors['name_ar']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, nameAr: value }));
          }}
        />

        <label className={styles['checkboxRow']}>
          <input
            type="checkbox"
            className={styles['checkbox']}
            checked={state.isActive}
            onChange={(event) => {
              const { checked } = event.target;
              setState((current) => ({ ...current, isActive: checked }));
            }}
          />
          {t('reference.fieldActive')}
        </label>

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('reference.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('reference.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
